/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CSSProperties } from "react";

import { css, cx } from "@panda/css";
import { ColorToken, token } from "@panda/tokens";
import { SystemStyleObject } from "@panda/types";

import { CosmosTextAs, CosmosTextVariantEnum, CosmosTextVariants } from "./text.types";
import { getTextAs, getTextVariantType } from "./text.utils";

export interface CosmosTextVariantProps {
	variant?: CosmosTextVariants | null;
}

export interface CosmosTextProps extends CosmosTextVariantProps {
	text?: string | null;
	children?: React.ReactNode;
	as?: CosmosTextAs;
	className?: string;
	color?: ColorToken;
	textCss?: SystemStyleObject;
	style?: CSSProperties;
}

interface CssVariables extends CSSProperties {
	"--color"?: string;
}

export const getPlainText = (input?: string): string => {
	if (!input) return "";
	let output = input;
	output = output.replace(/&nbsp;/gi, " "); // replace non-breaking space with space
	output = output.replace(/&#8209;/g, "-"); // replace non-breaking hyphen with hyphen
	output = output.replace(/&[#a-z0-9]{2,5};/gi, ""); // remove most other HTML entities
	output = output.replace(/<\/?[^>]+(>|$)/g, ""); // remove any HTML tags
	output = output.replace(/\s+/g, " "); // replace multiple whitespace characters with space
	return output.trim(); // trim any extra spaces at the beginning or end
};

export const CosmosText = (props: CosmosTextProps): React.JSX.Element | null => {
	const plainText = getPlainText(props?.text || "");
	if (!plainText && !props.children) {
		return null;
	}

	const as = getTextAs(props);
	const Component = as || "div";

	const variantType = getTextVariantType(props.variant);

	const style: CssVariables = props.style || {};
	if (props.color) {
		style["--color"] = token(`colors.${props.color}`);
	}

	return (
		<Component
			className={cx(
				"cosmos-text",
				variantType,
				css(
					{
						textStyle: props.variant || CosmosTextVariantEnum.paragraphMD,
						overflowWrap: "anywhere",
						color: "var(--color)",
					},
					props.textCss,
				),
				props.className || "",
			)}
			style={style}
		>
			{plainText ? plainText : props.children}
		</Component>
	);
};
